import { useState, Dispatch, SetStateAction, useEffect } from 'react'

// -------------------------------------------------------------

function useLocalStorage<T>(key: string, initialValue: T, isString = false) {
  const readValue = () => {
    if (typeof window === 'undefined') return initialValue
    try {
      const item = window.localStorage.getItem(key)
      return item ? (isString ? item : JSON.parse(item)) : initialValue
    } catch {
      return initialValue
    }
  }

  const [storedValue, setStoredValue] = useState(readValue)

  useEffect(() => {
    setStoredValue(readValue())
  }, [key]) // re-run when key changes (e.g., when user ID is set)

  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      // Silent fail
    }
  }

  const removeValue = () => {
    try {
      setStoredValue(initialValue)
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key)
      }
    } catch (error) {
      // Silent fail
    }
  }

  return [storedValue, setValue, removeValue]
}

export default useLocalStorage
